'use memo';

import { Link, createFileRoute } from '@tanstack/react-router';
import { CategoryScale, Chart as ChartJS, Legend, LineElement, LinearScale, PointElement, Title, Tooltip } from 'chart.js';
import type { ReactNode } from 'react';
import { Line } from 'react-chartjs-2';
import { StatCard, StatCardSkeleton, StatCardTitle } from '@/components/Card/StatCard';
import { Highlight, StatCardBase } from '@/components/Card/StatCard/variation/StatCardBase';
import { StatCardCount } from '@/components/Card/StatCard/variation/StatCardCount';
import { Grid, HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { useFormatDate } from '@/hooks/date';
import type { IdManagementOverviewCount, IdOverviewIndividualCategory, IdOverviewRecentItem } from '@/lib/api/orpc/contracts/id-overview';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useIdOverviewCount } from './-id-overview/useIdOverview';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
export const Route = createFileRoute('/_staff/ids/_layout/_placeholders/overview')({
  component: IdOverview
});

/**
 * ID 管理 Overview（ドラフト版）。
 *
 * - Row 1: ID 合計 / 個人ID 内訳 / グループID 件数
 * - Row 2: 最近追加されたID / 登録推移（登録数の月次累積ライン）
 *
 * データは oRPC 集約エンドポイント `/v2/id-management/overview/count`（BE: 既存 count usecase を再利用）から取得する。
 */
export function IdOverview() {
  const {
    t
  } = useTranslation(defaultNS);
  const {
    data,
    isLoading
  } = useIdOverviewCount();

  // 既存タブラベルを再利用（新規翻訳キーは追加しない）
  const individualLabel = t($ => $.common.list.idLists);
  const groupLabel = t($ => $.common.list.groupId);
  const seeMoreLabel = t($ => $.pages.idManagements.overview.seeMore);

  // 内訳カテゴリ → 既存タブラベル。動的キーは使わずカテゴリごとに selector を明示する。
  const categoryLabel = (category: IdOverviewIndividualCategory): string => {
    switch (category) {
      case 'EMPLOYEE_ID':
        return t($ => $.pages.idManagements.list.tabs.employeeId);
      case 'BOARD_MEMBER_ID':
        return t($ => $.pages.idManagements.list.tabs.boardMemberId);
      case 'CONTRACTOR_ID':
        return t($ => $.pages.idManagements.list.tabs.contractorId);
      case 'COLLABORATOR_ID':
        return t($ => $.pages.idManagements.list.tabs.collaboratorId);
      case 'PERSONAL_ID':
        return t($ => $.pages.idManagements.list.tabs.personalId);
    }
  };
  if (isLoading) {
    return <VStack className="gap-6 px-12 py-5">
        <Grid columns={3} columnGap={24} rowGap={24}>
          <StatCardSkeleton className="min-h-[240px]" />
          <StatCardSkeleton className="min-h-[240px]" />
          <StatCardSkeleton className="min-h-[240px]" />
        </Grid>
        <Grid columns={2} columnGap={24} rowGap={24}>
          <StatCardSkeleton className="min-h-[340px]" />
          <StatCardSkeleton className="min-h-[340px]" />
        </Grid>
      </VStack>;
  }
  const total = data?.total ?? 0;
  const individualTotal = data?.individualTotal ?? 0;
  const groupTotal = data?.groupTotal ?? 0;
  const recentGroups = data?.recentGroups ?? [];
  // 「他N件」は総数(groupTotal)基準（表示中の上位N件を差し引く）
  const restGroupCount = Math.max(0, groupTotal - recentGroups.length);
  const breakdownData = (data?.individualBreakdown ?? []).map(item => ({
    // ラベルは ReactNode で渡し、StatCardCount 既定の w-20 切り詰めを回避（全文表示）。
    // テーブル列は min-content のため、最長ラベルに合わせて幅が広がりバーは揃う。
    label: <Text variant="pSmall" className="whitespace-nowrap">
        {categoryLabel(item.category)}
      </Text>,
    value: item.count
  }));
  return <VStack className="gap-6 px-12 py-5">
      {/* Row 1: 件数 */}
      <Grid columns={3} columnGap={24} rowGap={24}>
        {/* Slot 1: ID 合計（個人ID + グループID の構成） */}
        <StatCardCount className="min-h-[240px]" title={t($ => $.pages.idManagements.list.pageTitle)} highlight={{
        label: total.toLocaleString()
      }} data={[{
        label: individualLabel,
        value: individualTotal
      }, {
        label: groupLabel,
        value: groupTotal
      }]} />

        {/* Slot 2: 個人ID（カテゴリ別内訳） */}
        <StatCardCount className="min-h-[240px]" title={individualLabel} action={<SeeMore to="/ids" label={seeMoreLabel} />} highlight={{
        label: individualTotal.toLocaleString()
      }} data={breakdownData} />

        {/* Slot 3: グループID（最近のグループID一覧。各行クリックで詳細へ） */}
        <StatCardBase className="min-h-[240px]" title={groupLabel} action={<SeeMore to="/ids/group-id" label={seeMoreLabel} />} highlight={<Highlight label={groupTotal.toLocaleString()} />}>
          {recentGroups.length > 0 ? <VStack className="mt-1 gap-0">
              {recentGroups.map(g => <Link key={g.proxyUserId} to="/ids/group-id/$id/detail" params={{
            id: g.proxyUserId
          }} className="flex items-center justify-between gap-3 rounded-md border-t border-neutral-muted-fill px-1 py-2 first:border-t-0 hover:bg-neutral-fill-hovered">
                  <Text variant="label" truncate className="min-w-0 flex-1 text-neutral-on-fill">
                    {g.name}
                  </Text>
                  <Text variant="labelSmallReg" className="shrink-0 whitespace-nowrap text-neutral-muted-on-fill">
                    {t('pages.idManagements.overview.memberCount', {
                count: g.memberCount
              })}
                  </Text>
                </Link>)}
              {restGroupCount > 0 ? <Text variant="labelSmallReg" className="mt-2 block text-neutral-muted-on-fill">
                  {t('pages.idManagements.overview.othersCount', {
              count: restGroupCount
            })}
                </Text> : null}
            </VStack> : null}
        </StatCardBase>
      </Grid>

      {/* Row 2: 動き（登録推移 / 最近追加） */}
      <Grid columns={2} columnGap={24} rowGap={24}>
        <TimeSeriesCard title={t($ => $.pages.idManagements.overview.timeSeries.title)} cumulativeLabel={t($ => $.pages.idManagements.overview.timeSeries.cumulativeLabel)} monthAxisLabel={t($ => $.pages.idManagements.overview.timeSeries.monthAxis)} empty={t($ => $.pages.idManagements.overview.timeSeries.empty)} monthly={data?.monthly ?? []} />
        <RecentCard title={t($ => $.pages.idManagements.overview.recent.title)} empty={t($ => $.pages.idManagements.overview.recent.empty)} recent={data?.recent ?? []} />
      </Grid>
    </VStack>;
}

/** カード右上の「もっと見る」導線。 */
function SeeMore(props: {
  to: '/ids' | '/ids/group-id';
  label: string;
}) {
  return <Button asChild variant="mutedGhost" size="sm" className="-mr-2 shrink-0">
      <Link to={props.to}>{props.label}</Link>
    </Button>;
}

/** Overview の 1 タイル分のカードシェル（タイトル + 中身）。 */
function OverviewCard(props: {
  title: string;
  children: ReactNode;
}) {
  return <StatCard className="flex h-full min-h-[340px] flex-col">
      <HStack className="min-h-8 items-center">
        <StatCardTitle>{props.title}</StatCardTitle>
      </HStack>
      {props.children}
    </StatCard>;
}

/** 最近追加されたID: loginId + 作成日時のリスト。 */
function RecentCard(props: {
  title: string;
  empty: string;
  recent: IdOverviewRecentItem[];
}) {
  const {
    title,
    empty,
    recent
  } = props;
  const formatDate = useFormatDate();
  return <OverviewCard title={title}>
      <VStack className="mt-2 min-h-0 flex-1 gap-0 overflow-y-auto">
        {recent.length === 0 ? <EmptyState message={empty} /> : recent.map(item => <HStack key={item.proxyUserId} className="items-center justify-between gap-3 border-t border-neutral-muted-fill px-1 py-2.5 first:border-t-0">
              <Text variant="label" truncate className="text-neutral-on-fill">
                {item.loginId}
              </Text>
              <Text variant="labelSmallReg" className="shrink-0 whitespace-nowrap text-neutral-muted-on-fill">
                {formatDate(item.occurredAt, {
            preset: 'dateWeekday'
          })}
              </Text>
            </HStack>)}
      </VStack>
    </OverviewCard>;
}

/** 登録推移: created_at 基準の月次累計 ID 数を折れ線で描く。 */
function TimeSeriesCard(props: {
  title: string;
  cumulativeLabel: string;
  monthAxisLabel: string;
  empty: string;
  monthly: IdManagementOverviewCount['monthly'];
}) {
  const {
    title,
    cumulativeLabel,
    monthAxisLabel,
    empty,
    monthly
  } = props;
  const chartData = {
    labels: monthly.map((m, i) => i === monthly.length - 1 ? `${m.month.slice(5)} ${monthAxisLabel}` : m.month.slice(5)),
    datasets: [{
      label: cumulativeLabel,
      data: monthly.map(m => m.cumulativeCount),
      borderColor: '#475569',
      backgroundColor: 'rgba(71, 85, 105, 0.1)',
      tension: 0.3,
      fill: false
    }]
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 16,
        right: 16,
        bottom: 8,
        left: 8
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.08)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true
      }
    }
  };
  return <OverviewCard title={title}>
      <div className="mt-2 min-h-0 flex-1 overflow-hidden px-1 pb-1">
        {monthly.length === 0 ? <EmptyState message={empty} /> : <Line data={chartData} options={chartOptions} />}
      </div>
    </OverviewCard>;
}
function EmptyState({
  message
}: {
  message: string;
}) {
  return <VStack className="h-full items-center justify-center">
      <Text variant="pSmall" className="text-neutral-muted-on-fill">
        {message}
      </Text>
    </VStack>;
}